import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { recordsApi } from '../../services/backendApi';
import { Alert, Button, Card, CardBody, EvidenceViewer, KindBadge, SitePill, Spinner, VerificationBadge, VerifyModal } from '../../components';
import type { VerifyTarget } from '../../components';
import { DirectoryFilterBar } from '../../components/DirectoryFilterBar';
import { formatDate, passportItemPath, RECORD_KIND_ACCENT, siteDisplayName, tenantDisplayName } from '../../utils';
import { useTenants } from '../../contexts/TenantContext';
import type { ItemVerificationStatus, QueueEvidence, QueueItem, RecordKind } from '../../types';

const KIND_ORDER: RecordKind[] = [
  'qualification',
  'work_experience',
  'reference',
  'official_document',
];

interface WorkerGroup {
  key: string;
  userId: string;
  profileId?: string | null;
  name: string;
  tenantId?: string | null;
  siteId?: string | null;
  items: QueueItem[];
}

function groupByWorker(items: QueueItem[]): WorkerGroup[] {
  const map = new Map<string, WorkerGroup>();
  for (const item of items) {
    const key = item.profile_id || item.user_id;
    const existing = map.get(key);
    if (existing) {
      existing.items.push(item);
      continue;
    }
    map.set(key, {
      key,
      userId: item.user_id,
      profileId: item.profile_id,
      name: item.worker_name,
      tenantId: item.tenant_id,
      siteId: item.tenant_site_id,
      items: [item],
    });
  }

  return [...map.values()]
    .map(group => ({
      ...group,
      items: [...group.items].sort((a, b) => {
        const kindDiff = KIND_ORDER.indexOf(a.kind) - KIND_ORDER.indexOf(b.kind);
        if (kindDiff !== 0) return kindDiff;
        return a.title.localeCompare(b.title);
      }),
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export const VerifierQueue: React.FC = () => {
  const { tenants } = useTenants();
  const [params, setParams] = useSearchParams();
  const tenantId = params.get('tenant') ?? '';
  const siteId = params.get('site') ?? '';
  const search = params.get('q') ?? '';
  const stage = (params.get('stage') as ItemVerificationStatus | null) ?? '';

  const [items, setItems] = useState<QueueItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [verifyTarget, setVerifyTarget] = useState<VerifyTarget | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [viewer, setViewer] = useState<{
    files: QueueEvidence[];
    title: string;
    itemHref: string | null;
  } | null>(null);
  const navigate = useNavigate();

  const setFilter = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key === 'tenant') next.delete('site');
    setParams(next, { replace: true });
  };

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setLoading(true);
      recordsApi
        .queue({
          tenantId: tenantId || undefined,
          siteId: siteId || undefined,
          name: search || undefined,
          status: stage || undefined,
        })
        .then(result => setItems(result ?? []))
        .catch(() => setError('Failed to load verification queue'))
        .finally(() => setLoading(false));
    }, 250);
    return () => window.clearTimeout(handle);
  }, [tenantId, siteId, search, stage]);

  const workers = useMemo(() => groupByWorker(items), [items]);

  const resultLabel = `${workers.length} worker${workers.length === 1 ? '' : 's'} · ${items.length} item${items.length === 1 ? '' : 's'}${
    tenantId ? ` · ${tenantDisplayName(tenants, tenantId)}` : ''
  }${siteId ? ` · ${siteDisplayName(tenants, tenantId, siteId)}` : ''}`;

  const openItem = (item: QueueItem) => {
    const href = item.profile_id
      ? passportItemPath(item.profile_id, item.kind, item.id)
      : null;
    if (item.evidence?.length) {
      setViewer({ files: item.evidence, title: item.title, itemHref: href });
      return;
    }
    if (href) navigate(href);
  };

  const submitVerify = async (basis: string) => {
    if (!verifyTarget) return;
    setSubmitting(true);
    try {
      await recordsApi.verify(verifyTarget.kind, verifyTarget.id, basis);
      setItems(current => current.filter(item => item.id !== verifyTarget.id));
      setVerifyTarget(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to verify');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Verification queue</h1>
        <p className="text-sm text-gray-500 mt-1">
          Items from workers in the tenants and sites you can access. Employer acceptance is optional.
        </p>
      </div>

      {error && <Alert variant="danger" onClose={() => setError(null)}>{error}</Alert>}

      <DirectoryFilterBar
        search={search}
        onSearch={value => setFilter('q', value)}
        fieldFilter=""
        onFieldFilter={() => undefined}
        tenantId={tenantId}
        onTenantId={value => setFilter('tenant', value)}
        siteId={siteId}
        onSiteId={value => setFilter('site', value)}
        showTenant
        hideField
        stage={stage}
        onStage={value => setFilter('stage', value)}
        resultLabel={resultLabel}
        onClear={() => setParams({}, { replace: true })}
        hasActiveFilters={Boolean(tenantId || siteId || search || stage)}
      />

      {loading ? (
        <div className="flex justify-center py-16"><Spinner size="lg" /></div>
      ) : workers.length === 0 ? (
        <Card>
          <CardBody>
            <div className="text-center py-12">
              <p className="text-gray-500 font-medium">No items ready to verify</p>
              <Link to="/verifier/workers" className="text-sm text-brand-600 mt-2 inline-block hover:text-brand-800">
                Browse all workers
              </Link>
            </div>
          </CardBody>
        </Card>
      ) : (
        <div className="space-y-4">
          {workers.map(worker => (
            <WorkerQueueCard
              key={worker.key}
              worker={worker}
              submitting={submitting}
              verifyTargetId={verifyTarget?.id}
              onVerify={setVerifyTarget}
              onOpenItem={openItem}
            />
          ))}
        </div>
      )}

      {viewer && (
        <EvidenceViewer
          files={viewer.files}
          title={viewer.title}
          itemHref={viewer.itemHref}
          onClose={() => setViewer(null)}
        />
      )}

      {verifyTarget && (
        <VerifyModal
          target={verifyTarget}
          submitting={submitting}
          onClose={() => setVerifyTarget(null)}
          onSubmit={submitVerify}
        />
      )}
    </div>
  );
};

const EYE_ICON =
  'M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z';

const WorkerQueueCard: React.FC<{
  worker: WorkerGroup;
  submitting: boolean;
  verifyTargetId?: string;
  onVerify: (target: VerifyTarget) => void;
  onOpenItem: (item: QueueItem) => void;
}> = ({ worker, submitting, verifyTargetId, onVerify, onOpenItem }) => {
  const { tenants } = useTenants();
  const tenantName = tenantDisplayName(tenants, worker.tenantId);
  const itemLabel = `${worker.items.length} item${worker.items.length === 1 ? '' : 's'}`;
  const initial = worker.name.trim().charAt(0).toUpperCase() || '?';

  return (
    <div className="bg-white border border-brand-border rounded-xl overflow-hidden">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 px-4 py-3.5 bg-brand-50/70 border-b border-brand-border">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className="w-10 h-10 rounded-full bg-brand-100 text-brand-800 flex items-center justify-center font-semibold flex-shrink-0">
            {initial}
          </div>
          <div className="min-w-0">
            {worker.profileId ? (
              <Link
                to={`/verifier/workers/${worker.profileId}`}
                className="font-semibold text-gray-900 hover:text-brand-800 truncate block"
              >
                {worker.name}
              </Link>
            ) : (
              <p className="font-semibold text-gray-900 truncate">{worker.name}</p>
            )}
            <div className="flex flex-wrap items-center gap-2 mt-1">
              <span className="text-xs text-gray-500">{tenantName}</span>
              <SitePill tenantId={worker.tenantId} siteId={worker.siteId} />
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0 sm:pl-2">
          <span className="text-xs font-medium text-brand-800 bg-white border border-brand-border px-2 py-0.5 rounded-full">
            {itemLabel}
          </span>
          {worker.profileId && (
            <Link
              to={`/verifier/workers/${worker.profileId}`}
              className="text-sm font-semibold text-brand-700 hover:text-brand-900"
            >
              Open passport
            </Link>
          )}
        </div>
      </div>

      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="bg-white text-gray-500 text-[11px] uppercase tracking-wide">
            <tr>
              <th className="px-4 py-2 font-semibold">Item</th>
              <th className="px-4 py-2 font-semibold">Title</th>
              <th className="px-4 py-2 font-semibold">Stage</th>
              <th className="px-4 py-2 font-semibold">Accepted</th>
              <th className="px-4 py-2 font-semibold text-right">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-brand-border">
            {worker.items.map(item => {
              const canOpen = Boolean(item.evidence?.length || item.profile_id);
              return (
                <tr
                  key={`${item.kind}-${item.id}`}
                  className={`border-l-4 ${RECORD_KIND_ACCENT[item.kind]} hover:bg-gray-50/80 ${canOpen ? 'cursor-pointer' : ''}`}
                  onClick={() => canOpen && onOpenItem(item)}
                >
                  <td className="px-4 py-3 whitespace-nowrap">
                    <KindBadge kind={item.kind} size="sm" />
                  </td>
                  <td className="px-4 py-3">
                    <span className={`font-medium ${canOpen ? 'text-brand-800 hover:underline' : 'text-gray-900'}`}>
                      {item.title}
                    </span>
                    {item.evidence && item.evidence.length > 1 && (
                      <span className="ml-2 text-[11px] text-gray-400">
                        {item.evidence.length} files
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <VerificationBadge status={item.verification_status} size="sm" />
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                    {item.confirmed_at ? formatDate(item.confirmed_at) : '—'}
                  </td>
                  <td className="px-4 py-3 text-right" onClick={e => e.stopPropagation()}>
                    <div className="inline-flex items-center justify-end gap-1">
                      {canOpen && (
                        <button
                          type="button"
                          onClick={() => onOpenItem(item)}
                          title={item.evidence?.length ? 'View evidence' : 'Open item'}
                          aria-label={item.evidence?.length ? 'View evidence' : 'Open item'}
                          className="p-2 rounded-lg text-brand-700 hover:bg-brand-50"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={EYE_ICON} />
                          </svg>
                        </button>
                      )}
                      {item.verification_status !== 'verified' && (
                        <Button
                          size="sm"
                          loading={submitting && verifyTargetId === item.id}
                          disabled={submitting}
                          onClick={() =>
                            onVerify({
                              kind: item.kind,
                              id: item.id,
                              title: item.title,
                              status: item.verification_status,
                            })
                          }
                        >
                          Verify
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="md:hidden divide-y divide-brand-border">
        {worker.items.map(item => {
          const canOpen = Boolean(item.evidence?.length || item.profile_id);
          return (
            <div
              key={`${item.kind}-${item.id}`}
              className={`px-4 py-3 space-y-2 border-l-4 ${RECORD_KIND_ACCENT[item.kind]}`}
            >
              <div className="min-w-0 space-y-2">
                <button
                  type="button"
                  disabled={!canOpen}
                  onClick={() => canOpen && onOpenItem(item)}
                  className="min-w-0 text-left disabled:cursor-default w-full"
                >
                  <KindBadge kind={item.kind} size="sm" />
                  <p className={`mt-1 font-medium break-words ${canOpen ? 'text-brand-800' : 'text-gray-900'}`}>{item.title}</p>
                </button>
                <VerificationBadge status={item.verification_status} size="sm" />
              </div>
              <div className="flex items-center justify-between gap-3">
                <p className="text-xs text-gray-500">
                  {item.confirmed_at ? `Accepted ${formatDate(item.confirmed_at)}` : 'Not yet accepted'}
                </p>
                <div className="flex items-center gap-1">
                  {canOpen && (
                    <button
                      type="button"
                      onClick={() => onOpenItem(item)}
                      title={item.evidence?.length ? 'View evidence' : 'Open item'}
                      aria-label={item.evidence?.length ? 'View evidence' : 'Open item'}
                      className="p-2 rounded-lg text-brand-700 hover:bg-brand-50"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={EYE_ICON} />
                      </svg>
                    </button>
                  )}
                  {item.verification_status !== 'verified' && (
                    <Button
                      size="sm"
                      loading={submitting && verifyTargetId === item.id}
                      disabled={submitting}
                      onClick={() =>
                        onVerify({
                          kind: item.kind,
                          id: item.id,
                          title: item.title,
                          status: item.verification_status,
                        })
                      }
                    >
                      Verify
                    </Button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
